/**
 * MockupConstructorLayout V2 — Reorganización del Paso 2 del ConstructorOferta.
 *
 *   - 2 columnas tituladas: VISIÓN MOBILSAFE (izq) · VISIÓN CLIENTE (der)
 *   - Sobre cada columna: zona KPI propia (3 tarjetas) con paleta distinta
 *     · MOBILSAFE → navy/azul (interno)
 *     · CLIENTE   → verde (visible cliente)
 *   - B3 renombrado a "Visión anual"
 *   - B4 renombrado a "Inversión y gasto cliente" y movido a la columna derecha · arriba
 *   - B1 incorpora 3ª columna "Total mes" (HW prorrateado al periodo + SaaS mes)
 *
 * Datos hardcodeados a propósito · sólo es validación visual previa al refactor real.
 */
import React from "react";
import {
  Plus, Printer, History, UserPlus, Check,
  Wallet, Coins, Target, Sparkles, TrendingUp, Percent,
} from "lucide-react";

// ============================================================================
// Datos mock (mismos valores que mockups anteriores para coherencia)
// ============================================================================
const MOCK = {
  cliente: "Borr Test Cliente",
  oferta: "Oferta #2026-0042",
  escenarioActivo: "Escenario A",
  escenarios: ["Escenario A", "Escenario B", "Escenario C"],
  flota: 43,
  meses: 36,
  // Zona KPI MOBILSAFE
  ingresoMensualMS: "843 €",   // cuota saas mes (cliente paga · MS factura)
  pctMBTotal: "37,6 %",
  ingresoTotalMS: "26.626 €",  // HW + SaaS × 36m
  // Zona KPI CLIENTE
  totalBeneficios: "+25.214 €",
  ahorroAreas: "51.840 €",
  caesNeto: "8.600 €",
  // B1
  porVehiculo: {
    venta:  ["90 €", "529 €", "619 €"],
    coste:  ["62 €", "324 €", "386 €"],
    mb:     ["28 €", "205 €", "233 €"],
    pctmb:  ["31,1 %", "38,7 %", "37,6 %"],
    check:  [true, false, true],
    // 3ª columna nueva: "Total mes" — HW prorrateado al periodo + SaaS mes
    totalMes: ["2,5 €", "529 €", "531,5 €"], // ejemplo (90€/36m + 529€)
  },
  porFlota: {
    venta:  ["3.870 €", "22.756 €", "26.626 €"],
    coste:  ["2.666 €", "13.932 €", "16.598 €"],
    mb:     ["1.204 €", "8.824 €",  "10.028 €"],
    pctmb:  ["31,1 %",  "38,7 %",   "37,6 %"],
    check:  [true, false, true],
  },
  visionAnual: {
    venta: ["12.840 €", "9.984 €", "832 €"],
    coste: ["8.310 €",  "5.808 €", "484 €"],
    mb:    ["4.530 €",  "4.176 €", "348 €"],
    pctmb: ["35,3 %",   "41,8 %",  "41,8 %"],
    check: [true, true, true],
  },
  // B4 Inversión y gasto cliente
  totalHW: "3.870 €",
  cuotaMes: "632 €",
  porVehMes: "14,7 €",
  // B5 Ahorro por área
  ahorros: [
    { area: "Combustible",    pct: "8,0 %",  euros: "7.420 €" },
    { area: "Mantenimiento",  pct: "5,5 %",  euros: "3.180 €" },
    { area: "Siniestralidad", pct: "12,0 %", euros: "5.450 €" },
    { area: "Multas",         pct: "20,0 %", euros: "1.230 €" },
  ],
  ahorroAnioTotal: "17.280 €",
  // B6 Balance
  invHW:          "-3.870 €",
  cuotaSaasPer:   "-22.756 €",
  ahorroPer:      "51.840 €",
  balance:        "+25.214 €",
  // B7 ROI
  mesesRecup:     "2,1 meses",
  ahorroMesNeto:  "+1.847 €",
  ahorroVehMes:   "+42,9 €",
  // B8 CAES
  caesCert:       "10.750 €",
  caesCom:        "-2.150 €",
};

// ============================================================================
// Helpers visuales
// ============================================================================
const Chip = ({ ok }) => (
  <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-[11px] font-bold ${
    ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
  }`}>{ok ? "OK" : "BAJO"}</span>
);

// Tarjeta KPI con dos paletas (mobilsafe · cliente) y misma dinámica
function KPI({ icon: Icon, label, value, sub, paleta = "ms", destacado = false }) {
  const palettes = {
    ms: {
      wrapBg: destacado ? "bg-[#1e3a5f] text-white" : "bg-white",
      border: "border-[#1e3a5f]/30",
      icon:   destacado ? "text-amber-300" : "text-[#1e3a5f]",
      label:  destacado ? "text-white/70" : "text-[#1e3a5f]/70",
      value:  destacado ? "text-white" : "text-[#1e3a5f]",
      sub:    destacado ? "text-white/60" : "text-slate-400",
    },
    cliente: {
      wrapBg: destacado ? "bg-[#0a6640] text-white" : "bg-white",
      border: "border-[#0a6640]/30",
      icon:   destacado ? "text-amber-300" : "text-[#0a6640]",
      label:  destacado ? "text-white/70" : "text-[#0a6640]/70",
      value:  destacado ? "text-white" : "text-[#0a6640]",
      sub:    destacado ? "text-white/60" : "text-slate-400",
    },
  };
  const p = palettes[paleta];
  return (
    <div className={`rounded-lg border-2 ${p.border} ${p.wrapBg} px-4 py-3 transition`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${p.icon}`} />
        <div className={`text-[10px] uppercase tracking-wider font-bold ${p.label}`}>{label}</div>
      </div>
      <div className={`text-2xl font-black tabular-nums mt-1 ${p.value}`}>{value}</div>
      {sub && <div className={`text-[11px] mt-0.5 ${p.sub}`}>{sub}</div>}
    </div>
  );
}

// Tabla compacta cliente · misma forma que las BloqueTabla reales
function BloqueTabla({ titulo, paletaHeader, headers, rows }) {
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div className={`px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center justify-between ${paletaHeader}`}>
        <span>{titulo}</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b">
            {headers.map((h, i) => (
              <th key={i} className={`px-3 py-1.5 text-[11px] font-semibold text-slate-500 uppercase ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={`border-t ${r.ultima ? (r.verde ? "bg-emerald-50/70 font-bold" : r.rojo ? "bg-rose-50/70 font-bold" : "bg-slate-100 font-bold") : i % 2 ? "bg-slate-50/40" : "bg-white"} ${r.mb ? "bg-amber-50/70" : ""}`}>
              {r.cols.map((c, j) => (
                <td key={j} className={`px-3 py-1.5 ${j === 0 ? "text-left text-slate-700 font-medium" : "text-right tabular-nums"} ${r.verde && j > 0 ? "text-emerald-700" : r.rojo && j > 0 ? "text-rose-700" : "text-slate-800"} ${r.ultima ? "text-base" : ""} ${r.mb ? "font-bold" : ""}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Título de columna grande, alineados horizontalmente entre izq/der
function TituloColumna({ icon: Icon, texto, paleta }) {
  const palettes = {
    ms: "bg-[#1e3a5f] text-white",
    cliente: "bg-[#0a6640] text-white",
  };
  return (
    <div className={`${palettes[paleta]} rounded-lg px-5 py-3 flex items-center gap-3 shadow`}>
      <Icon className="w-5 h-5" />
      <h2 className="text-base font-black uppercase tracking-widest">{texto}</h2>
    </div>
  );
}

// ============================================================================
// Componente principal
// ============================================================================
export default function MockupConstructorLayout() {
  return (
    <div className="min-h-screen bg-slate-50">

      {/* Header (Header B aprobado · sin toggle interno descartado) */}
      <header className="bg-white border-b sticky top-0 z-30 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="flex items-center justify-between py-3 border-b border-slate-100">
            <div className="flex items-center gap-4 flex-1">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Constructor de oferta</div>
                <h1 className="text-base font-bold text-slate-800">{MOCK.cliente} · {MOCK.oferta}</h1>
              </div>
              <div className="flex items-center gap-1 ml-4">
                {MOCK.escenarios.map((e, i) => (
                  <button key={i} className={`px-3 py-1 text-xs font-semibold rounded-md transition ${
                    e === MOCK.escenarioActivo ? "bg-[#0a6640] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}>{e}</button>
                ))}
                <button className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-md border-dashed border-2 text-slate-400 hover:bg-slate-50">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 rounded-md text-slate-500 hover:bg-slate-100" title="Historial"><History className="w-4 h-4" /></button>
              <button className="p-2 rounded-md text-slate-500 hover:bg-slate-100" title="Asignar"><UserPlus className="w-4 h-4" /></button>
              <button className="px-3 py-1.5 text-xs font-semibold bg-slate-900 text-white rounded-md inline-flex items-center gap-1.5"><Printer className="w-3.5 h-3.5" /> Imprimir</button>
            </div>
          </div>
          {/* Stepper */}
          <div className="flex items-center justify-center gap-2 py-3">
            <button className="px-4 py-1.5 text-sm font-semibold bg-slate-100 text-slate-500 rounded-md">Paso 1 · Datos</button>
            <span className="text-slate-300">›</span>
            <button className="px-4 py-1.5 text-sm font-bold bg-[#3730a3] text-white rounded-md shadow">Paso 2 · Números</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-6">

        {/* Banda Paso 2 */}
        <div className="w-full bg-[#eef2ff] border border-[#c7d2fe] rounded-lg px-5 py-3 flex items-center gap-3 mb-5">
          <span className="text-xs font-bold uppercase tracking-wide bg-[#3730a3] text-white px-3 py-1 rounded-md">Paso 2</span>
          <span className="text-sm font-medium text-[#374151]">Revisa los números internos y el argumento de venta al cliente</span>
        </div>

        {/* Toggle modalidad HW (estado actual) */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <span className="text-sm text-slate-600 font-medium">Modalidad HW:</span>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            <button className="px-4 py-2 text-sm font-medium bg-[#0a6640] text-white">Venta HW</button>
            <button className="px-4 py-2 text-sm font-medium bg-white text-slate-600">Financiación HW</button>
          </div>
        </div>

        {/* ========================================================================
            ZONAS KPI · 2 columnas alineadas con paletas distintas
        ======================================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">

          {/* KPIs MOBILSAFE · navy */}
          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#1e3a5f]/60 px-1">Indicadores internos</div>
            <div className="grid grid-cols-3 gap-3">
              <KPI icon={Wallet}     label="Ingreso mensual"  value={MOCK.ingresoMensualMS} sub="SaaS recurrente" paleta="ms" />
              <KPI icon={Percent}    label="% MB total"       value={MOCK.pctMBTotal}        sub="Margen bruto"    paleta="ms" />
              <KPI icon={TrendingUp} label="Ingreso total"    value={MOCK.ingresoTotalMS}   sub={`HW + SaaS · ${MOCK.meses}m`} paleta="ms" destacado />
            </div>
          </div>

          {/* KPIs CLIENTE · verde */}
          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#0a6640]/70 px-1">Argumento al cliente</div>
            <div className="grid grid-cols-3 gap-3">
              <KPI icon={Target}   label="Total beneficios" value={MOCK.totalBeneficios} sub={`Balance neto ${MOCK.meses}m`} paleta="cliente" destacado />
              <KPI icon={Coins}    label="Ahorros áreas"    value={MOCK.ahorroAreas}     sub={`Combust+Mant+Sin+Mul · ${MOCK.meses}m`} paleta="cliente" />
              <KPI icon={Sparkles} label="CAES neto"        value={MOCK.caesNeto}        sub="Año 1 · única"    paleta="cliente" />
            </div>
          </div>
        </div>

        {/* ========================================================================
            TÍTULOS DE COLUMNA · alineados
        ======================================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
          <TituloColumna icon={Target}   texto="Visión MOBILSAFE" paleta="ms" />
          <TituloColumna icon={Sparkles} texto="Visión cliente"   paleta="cliente" />
        </div>

        {/* ========================================================================
            CONTENIDO 2 COLUMNAS
        ======================================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ==== Columna IZQUIERDA · MOBILSAFE ==== */}
          <div className="space-y-4">
            {/* B1 · Operación por vehículo + Total mes */}
            <BloqueTabla
              titulo="Operación por vehículo"
              paletaHeader="bg-orange-100 text-orange-900"
              headers={["", "HW", "SaaS", `Total mes (×${MOCK.meses}m)`]}
              rows={[
                { cols: ["Venta", MOCK.porVehiculo.venta[0], MOCK.porVehiculo.venta[1], MOCK.porVehiculo.totalMes[2]] },
                { cols: ["Coste", MOCK.porVehiculo.coste[0], MOCK.porVehiculo.coste[1], "—"] },
                { cols: ["MB",    MOCK.porVehiculo.mb[0],    MOCK.porVehiculo.mb[1],    "—"], mb: true },
                { cols: ["%MB",   MOCK.porVehiculo.pctmb[0], MOCK.porVehiculo.pctmb[1], MOCK.porVehiculo.pctmb[2]] },
                { cols: ["Check", <Chip key="a" ok={MOCK.porVehiculo.check[0]} />, <Chip key="b" ok={MOCK.porVehiculo.check[1]} />, <Chip key="c" ok={MOCK.porVehiculo.check[2]} />], ultima: true },
              ]}
            />
            {/* B2 · Operación por flota */}
            <BloqueTabla
              titulo={`Operación por flota · ${MOCK.flota} veh · ${MOCK.meses}m`}
              paletaHeader="bg-blue-100 text-blue-900"
              headers={["", "HW", "SaaS", "Total"]}
              rows={[
                { cols: ["Venta", ...MOCK.porFlota.venta] },
                { cols: ["Coste", ...MOCK.porFlota.coste] },
                { cols: ["MB",    ...MOCK.porFlota.mb], mb: true },
                { cols: ["%MB",   ...MOCK.porFlota.pctmb] },
                { cols: ["Check", <Chip key="a" ok={MOCK.porFlota.check[0]} />, <Chip key="b" ok={MOCK.porFlota.check[1]} />, <Chip key="c" ok={MOCK.porFlota.check[2]} />], ultima: true },
              ]}
            />
            {/* B3 · Visión anual (antes "Visión Mobilsafe") */}
            <BloqueTabla
              titulo="Visión anual"
              paletaHeader="bg-emerald-100 text-emerald-900"
              headers={["", "Año 1", "Año 2", "Mes"]}
              rows={[
                { cols: ["Venta", ...MOCK.visionAnual.venta] },
                { cols: ["Coste", ...MOCK.visionAnual.coste] },
                { cols: ["MB",    ...MOCK.visionAnual.mb], mb: true },
                { cols: ["%MB",   ...MOCK.visionAnual.pctmb] },
                { cols: ["Check", <Chip key="a" ok={MOCK.visionAnual.check[0]} />, <Chip key="b" ok={MOCK.visionAnual.check[1]} />, <Chip key="c" ok={MOCK.visionAnual.check[2]} />], ultima: true },
              ]}
            />
          </div>

          {/* ==== Columna DERECHA · CLIENTE ==== */}
          <div className="space-y-4">
            {/* B4 · Inversión y gasto cliente (antes "Visión cliente · resumen") · MOVIDO ARRIBA */}
            <BloqueTabla
              titulo="Inversión y gasto cliente"
              paletaHeader="bg-[#0a6640] text-white"
              headers={["", "Inversión", "Mes"]}
              rows={[
                { cols: ["Total HW", MOCK.totalHW, "—"] },
                { cols: ["Total por mes", "—", MOCK.cuotaMes] },
                { cols: ["Por vehículo y mes", "—", MOCK.porVehMes], ultima: true },
              ]}
            />
            {/* B5 · Ahorro por área */}
            <BloqueTabla
              titulo="Ahorro por área · año"
              paletaHeader="bg-slate-100 text-slate-700"
              headers={["", "%", "Ahorro/año"]}
              rows={[
                ...MOCK.ahorros.map((a) => ({ cols: [a.area, a.pct, a.euros], verde: true })),
                { cols: ["Total ahorro/año", "", MOCK.ahorroAnioTotal], ultima: true, verde: true },
              ]}
            />
            {/* B6 · Balance cliente */}
            <BloqueTabla
              titulo={`Balance cliente · ${MOCK.meses}m`}
              paletaHeader="bg-slate-100 text-slate-700"
              headers={["", "€"]}
              rows={[
                { cols: ["Inversión HW", MOCK.invHW], rojo: true },
                { cols: [`Cuotas SaaS · ${MOCK.meses}m`, MOCK.cuotaSaasPer], rojo: true },
                { cols: [`Ahorro total · ${MOCK.meses}m`, MOCK.ahorroPer], verde: true },
                { cols: ["Balance neto", MOCK.balance], ultima: true, verde: true },
              ]}
            />
            {/* B7 · ROI y recuperación */}
            <BloqueTabla
              titulo="ROI y recuperación"
              paletaHeader="bg-violet-100 text-violet-900"
              headers={["", ""]}
              rows={[
                { cols: ["Recuperación inversión", MOCK.mesesRecup] },
                { cols: ["Ahorro mensual neto", MOCK.ahorroMesNeto], verde: true },
                { cols: ["Ahorro por veh. y mes", MOCK.ahorroVehMes], ultima: true, verde: true },
              ]}
            />
            {/* B8 · CAES */}
            <BloqueTabla
              titulo="Potencial CAES · Año 1 (certificación única)"
              paletaHeader="bg-[#0a6640] text-white"
              headers={["", "€"]}
              rows={[
                { cols: ["Certificados potenciales", MOCK.caesCert] },
                { cols: ["Comisión MOBILSAFE 20%", MOCK.caesCom], rojo: true },
                { cols: ["Ingreso neto cliente", MOCK.caesNeto], ultima: true, verde: true },
              ]}
            />
          </div>
        </div>

        {/* Resumen comparativo */}
        <div className="mt-8 p-4 rounded-xl bg-white border-2 border-dashed border-slate-300">
          <h3 className="text-sm font-bold text-slate-700 mb-2">Resumen de cambios respecto al estado actual</h3>
          <ul className="text-xs text-slate-600 space-y-1">
            <li>✅ <strong>2 zonas KPI</strong> alineadas con sus columnas · paletas navy (MS) / verde (cliente)</li>
            <li>✅ <strong>Títulos de columna</strong> grandes y alineados horizontalmente</li>
            <li>✅ <strong>B3 "Visión Mobilsafe" → "Visión anual"</strong></li>
            <li>✅ <strong>B4 "Visión cliente · resumen" → "Inversión y gasto cliente"</strong> · movido a la derecha · arriba del todo</li>
            <li>✅ <strong>B1 "Operación por vehículo"</strong> · añadida 3ª columna <em>Total mes</em> (HW prorrateado + SaaS mes)</li>
          </ul>
        </div>

      </main>
    </div>
  );
}
